import {Text} from "@unibuddy/patron";
import {useAnalytics} from "@unibuddy/tracking";
import {useFormikContext} from "formik";
import {useEffect} from "react";
import {TrackEvents, UniversityFeatureFlags} from "ubcommunity-shared/src/constants";
import {AutocompleteModal} from "ubcommunity-shared/src/General/AutocompleteModal/AutocompleteModal";
import {useUniversityFeature} from "ubcommunity-shared/src/Hooks/useUniversityFeature";
import {FormValues} from "ubcommunity-shared/src/SignUpStepTwo/SignUpStepTwo";
import {useDiscoverableUniversity} from "ubcommunity-shared/src/SignUpStepTwo/useDiscoverableUniversity";
import {useGetDomesticRegionByCountryIdQuery} from "ubcommunity-shared/src/types";

interface IEditRegionProps {
    universityId: string;
    selectedCountryId: string;
}

export const EditRegion = ({universityId, selectedCountryId}: IEditRegionProps) => {
    const {university} = useDiscoverableUniversity(universityId);
    const {isFeatureEnabled} = useUniversityFeature(university?.slug ?? "");
    const {trackEvent} = useAnalytics();
    const isDomesticRegionEnabled = isFeatureEnabled(
        UniversityFeatureFlags.COLLECT_DOMESTIC_REGION_ON_SIGNUP,
    );
    const {values, setFieldValue} = useFormikContext<FormValues>();
    const {data, loading, error} = useGetDomesticRegionByCountryIdQuery({
        variables: {countryId: selectedCountryId},
        skip: !isDomesticRegionEnabled || !selectedCountryId,
    });

    useEffect(() => {
        // Clear region value if present when selected country gets cleared
        if (!selectedCountryId && values.selectRegion.length > 0) {
            setFieldValue("selectRegion", []);
        }
    }, [selectedCountryId, values, setFieldValue]);

    // If feature is disabled, do not show input
    // Else, if feature is enabled:
    //      If user profile has country and it does NOT match university country, do not show
    //      If the user has not selected a country earlier in the form, do not show
    //      If the domestic region query is loading, has errored, or returns empty, do not show
    //      If all above do not apply, show the input
    if (
        !isDomesticRegionEnabled ||
        !selectedCountryId ||
        loading ||
        error ||
        data?.domesticRegions?.length === 0
    ) {
        return null;
    }

    const handleOnDialogOpened = () => {
        trackEvent(TrackEvents.SELECT_DOMESTIC_REGION_OPEN);
    };

    const handleOnDialogDismiss = () => {
        trackEvent(TrackEvents.SELECT_DOMESTIC_REGION_DISMISSED);
    };

    return (
        <AutocompleteModal
            name="selectRegion"
            label="Domestic Region"
            data={data?.domesticRegions}
            renderItem={(item) => <Text>{item?.name}</Text>}
            resultKey="name"
            onDropdownOpened={handleOnDialogOpened}
            onDismiss={handleOnDialogDismiss}
            placeholder="Select domestic region"
        />
    );
};
